'use client';

import { useState } from 'react';
import { Partner, PartnerStatus, Priority, STATUSES, CATEGORY_LABELS, DecisionMaker } from '@/types';

interface Props {
  partner: Partner;
  onClose: () => void;
  onSave: (id: string, patch: Partial<Partner>) => Promise<void>;
}

export default function PartnerDetailModal({ partner, onClose, onSave }: Props) {
  const [status, setStatus] = useState<PartnerStatus>(partner.status);
  const [priority, setPriority] = useState<Priority | ''>(partner.priority || '');
  const [probability, setProbability] = useState<string>(
    partner.probability != null ? String(partner.probability) : ''
  );
  const [introPath, setIntroPath] = useState(partner.intro_path || '');
  const [nextAction, setNextAction] = useState(partner.next_action || '');
  const [notes, setNotes] = useState(partner.notes || '');
  const [saving, setSaving] = useState(false);

  const cat = CATEGORY_LABELS[partner.category];
  const dms: DecisionMaker[] = partner.decision_makers || [];

  const handleSave = async () => {
    setSaving(true);
    await onSave(partner.id, {
      status,
      priority: (priority || null) as Priority | null,
      probability: probability === '' ? null : Math.max(0, Math.min(100, parseInt(probability, 10) || 0)),
      intro_path: introPath || null,
      next_action: nextAction || null,
      notes: notes || null,
    });
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{partner.partner}</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {cat?.emoji} {cat?.label || partner.category}
                {partner.country ? ` · ${partner.country}` : ''}
                {partner.partnership_type === 'local_operational' ? ' · local' : ' · estratégico'}
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {partner.website && (
            <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0890F1] hover:underline mt-2 inline-block">
              {partner.website.replace(/^https?:\/\//, '')} ↗
            </a>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Etapa</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as PartnerStatus)}
                className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5"
              >
                {STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Prioridad</span>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as Priority | '')}
                className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5"
              >
                <option value="">—</option>
                <option value="high">Alta</option>
                <option value="medium">Media</option>
                <option value="low">Baja</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Prob. %</span>
              <input
                type="number"
                min={0}
                max={100}
                value={probability}
                onChange={(e) => setProbability(e.target.value)}
                className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-gray-500">Intro path / cómo entrar</span>
            <input
              value={introPath}
              onChange={(e) => setIntroPath(e.target.value)}
              className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Warm intro, contacto, evento…"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-500">Próxima acción</span>
            <input
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
              className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
              placeholder="Ej: enviar email a Head of Loyalty"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-500">Notas</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none"
            />
          </label>

          {/* Decision makers (poblados en Fase 2 / a mano) */}
          <div>
            <span className="text-xs font-medium text-gray-500">Decision makers ({dms.length})</span>
            {dms.length === 0 ? (
              <p className="text-xs text-gray-400 mt-1">Aún sin contactos.</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {dms.map((dm, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-center justify-between gap-2 border border-gray-100 rounded-lg px-2 py-1">
                    <span>
                      <span className="font-medium">{dm.fullName || dm.email}</span>
                      {dm.position ? <span className="text-gray-500"> · {dm.position}</span> : null}
                    </span>
                    {dm.linkedin && (
                      <a href={dm.linkedin} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0890F1] hover:underline flex-shrink-0">in ↗</a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-[#0890F1] hover:bg-[#0770C5] text-white rounded-lg font-medium disabled:opacity-50"
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
